/**
 * Shared Token Refresh Service
 *
 * Singleton module used by both the HTTP layer (base-query.ts) and the
 * WebSocket layer (socket-manager) so that:
 *  - Only ONE refresh request is in-flight at a time (deduplication).
 *  - Both layers get the same fresh token.
 *  - Force-logout is triggered consistently when the refresh token itself
 *    is expired / invalid.
 */

import { API_URLS, API_SETTINGS, AUTH_RETRY_CONFIG } from "@/api/config";
import {
  setAccessToken,
  setRefreshToken,
  setProjectAccessToken,
  setProjectRefreshToken,
  setProjectTokenClaims,
  updateUserDetailsFromToken,
} from "@/store/slices/auth";
import { extractUserDataFromToken, decodeProjectTokenClaims } from "@/api/utils";
import type { RootState } from "@/store/types";

// ── Types ────────────────────────────────────────────────────────────────────

export type RefreshFailureReason =
  | "TOKEN_INVALID"
  | "TOKEN_FORBIDDEN"
  | "NO_CREDENTIALS"
  | "SERVER_ERROR"
  | "NETWORK_ERROR"
  | "PARSE_ERROR"
  | "UNKNOWN";

export interface RefreshAttemptResult {
  success: boolean;
  shouldRetry: boolean;
  reason?: RefreshFailureReason;
  token?: string;
}

export interface RefreshResult {
  success: boolean;
  token?: string;
  reason?: RefreshFailureReason;
}

export type AuthTokenMode = "auto" | "identity" | "project";

/**
 * Minimal store-like interface so this module stays decoupled from the
 * concrete Redux store import (avoids circular deps when base-query
 * imports us).
 */
export interface StoreApi {
  getState: () => RootState;
  dispatch: (action: unknown) => void;
}

// ── Internal state ───────────────────────────────────────────────────────────

const refreshPromises: Partial<Record<AuthTokenMode, Promise<RefreshResult>>> =
  {};

interface RefreshSessionState {
  consecutiveFailures: number;
  lastSuccessTimestamp: number;
  isRefreshing: boolean;
}

const refreshState: RefreshSessionState = {
  consecutiveFailures: 0,
  lastSuccessTimestamp: Date.now(),
  isRefreshing: false,
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const calculateBackoffDelay = (attempt: number): number => {
  const delay =
    AUTH_RETRY_CONFIG.INITIAL_DELAY_MS *
    Math.pow(AUTH_RETRY_CONFIG.BACKOFF_MULTIPLIER, attempt);
  return Math.min(delay, AUTH_RETRY_CONFIG.MAX_DELAY_MS);
};

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Reset the failure counter — call on successful login or manual refresh.
 */
export const resetAuthFailureCounter = (): void => {
  refreshState.consecutiveFailures = 0;
  refreshState.lastSuccessTimestamp = Date.now();
  if (API_SETTINGS.debugMode) {
    console.log("[TokenRefresh] Failure counter reset");
  }
};

/**
 * Single-attempt token refresh. Does NOT retry — the caller
 * (`performTokenRefresh`) handles retry logic.
 */
const attemptTokenRefresh = async (
  api: StoreApi,
  authMode: AuthTokenMode,
): Promise<RefreshAttemptResult> => {
  try {
    const state = api.getState();
    const projectRefreshToken = state.auth?.projectRefreshToken;
    const loginRefreshToken = state.auth?.loginResponse?.refreshToken;
    const refreshTokenToUse =
      authMode === "identity"
        ? loginRefreshToken
        : authMode === "project"
          ? projectRefreshToken
          : projectRefreshToken || loginRefreshToken;
    const isUsingProjectToken =
      authMode === "project" ||
      (authMode === "auto" &&
        !!projectRefreshToken &&
        refreshTokenToUse === projectRefreshToken);

    if (!refreshTokenToUse) {
      console.warn("[TokenRefresh] No refresh token available");
      return { success: false, shouldRetry: false, reason: "NO_CREDENTIALS" };
    }

    if (API_SETTINGS.debugMode) {
      console.log(
        `[TokenRefresh] Attempting refresh (${isUsingProjectToken ? "project" : "login"} token)…`,
      );
    }

    const response = await fetch(`${API_URLS.auth}auth/refreshToken`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: refreshTokenToUse }),
    });

    // ── Success ──────────────────────────────────────────────────────────
    if (response.ok) {
      try {
        const data = await response.json();
        if (data?.token) {
          api.dispatch(setAccessToken(data.token));
          if (data.refreshToken) {
            api.dispatch(setRefreshToken(data.refreshToken));
          }

          // Dispatch tokens into Redux
          if (isUsingProjectToken) {
            api.dispatch(setProjectAccessToken(data.token));
            const claims = decodeProjectTokenClaims(data.token);
            api.dispatch(setProjectTokenClaims(claims));
            if (data.refreshToken) {
              api.dispatch(setProjectRefreshToken(data.refreshToken));
            }
          }

          // Update user details
          const userData = extractUserDataFromToken(data.token);
          if (userData) {
            api.dispatch(
              updateUserDetailsFromToken({
                firstName: userData.firstName,
                lastName: userData.lastName,
                email: userData.email,
                role: userData.role,
                isAgent: userData.isAgent,
                status: userData.status,
                ciNo: userData.ciNo,
                profilePicUrl: userData.profilePicUrl,
              }),
            );
          }

          resetAuthFailureCounter();
          console.log(
            `[TokenRefresh] Success (${isUsingProjectToken ? "project" : "login"} token)`,
          );
          return { success: true, shouldRetry: false, token: data.token };
        }
        console.warn("[TokenRefresh] Response OK but no token");
        return { success: false, shouldRetry: true, reason: "PARSE_ERROR" };
      } catch {
        console.warn("[TokenRefresh] Failed to parse response");
        return { success: false, shouldRetry: true, reason: "PARSE_ERROR" };
      }
    }

    // ── Non-retryable failures ───────────────────────────────────────────
    if (response.status === 401) {
      console.error("[TokenRefresh] Refresh token expired/invalid (401)");
      return { success: false, shouldRetry: false, reason: "TOKEN_INVALID" };
    }
    if (response.status === 403) {
      console.error("[TokenRefresh] Refresh token forbidden (403)");
      return { success: false, shouldRetry: false, reason: "TOKEN_FORBIDDEN" };
    }
    if (response.status >= 400 && response.status < 500) {
      console.warn(`[TokenRefresh] Client error (${response.status})`);
      return { success: false, shouldRetry: false, reason: "UNKNOWN" };
    }

    // ── Retryable failures ───────────────────────────────────────────────
    if (response.status >= 500) {
      console.warn(`[TokenRefresh] Server error (${response.status})`);
      return { success: false, shouldRetry: true, reason: "SERVER_ERROR" };
    }

    console.warn(`[TokenRefresh] Unexpected status (${response.status})`);
    return { success: false, shouldRetry: true, reason: "UNKNOWN" };
  } catch (error) {
    const msg = error instanceof Error ? error.message : "Unknown error";
    console.warn(`[TokenRefresh] Network error: ${msg}`);
    return { success: false, shouldRetry: true, reason: "NETWORK_ERROR" };
  }
};

/**
 * Perform a token refresh with exponential-backoff retry.
 *
 * **Deduplication**: If a refresh is already in-flight (from either the
 * HTTP layer or the socket layer), callers share the same promise.
 *
 * Returns `{ success, token?, reason? }`.
 */
export const performTokenRefresh = async (
  api: StoreApi,
  authMode: AuthTokenMode = "auto",
): Promise<RefreshResult> => {
  // Deduplicate: if a refresh is already running, piggy-back on it
  const activeRefreshPromise = refreshPromises[authMode];
  if (activeRefreshPromise) {
    if (API_SETTINGS.debugMode) {
      console.log(
        `[TokenRefresh] ${authMode} refresh already in progress, waiting…`,
      );
    }
    return activeRefreshPromise;
  }

  refreshState.isRefreshing = true;

  const refreshPromise = (async (): Promise<RefreshResult> => {
    const maxRetries = AUTH_RETRY_CONFIG.MAX_RETRIES;
    let sessionFailures = 0;

    console.log(
      `[TokenRefresh] Starting ${authMode} refresh session (max ${maxRetries} retries)`,
    );

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      console.log(
        `[TokenRefresh] Attempt ${attempt + 1}/${maxRetries}`,
      );

      const result = await attemptTokenRefresh(api, authMode);

      if (result.success) {
        refreshState.consecutiveFailures = 0;
        refreshState.isRefreshing = false;
        return { success: true, token: result.token };
      }

      sessionFailures++;
      refreshState.consecutiveFailures++;

      // Non-retryable → bail immediately
      if (!result.shouldRetry) {
        console.error(
          `[TokenRefresh] Non-retryable failure (${result.reason})`,
        );
        refreshState.isRefreshing = false;
        return { success: false, reason: result.reason };
      }

      // Retryable → wait with backoff
      if (attempt < maxRetries - 1) {
        const delay = calculateBackoffDelay(attempt);
        console.log(
          `[TokenRefresh] Waiting ${delay}ms before retry ${attempt + 2}/${maxRetries}…`,
        );
        await sleep(delay);
      }
    }

    console.error(
      `[TokenRefresh] FAILED after ${maxRetries} retries (${sessionFailures} failures)`,
    );
    refreshState.isRefreshing = false;
    return { success: false, reason: "UNKNOWN" };
  })();

  refreshPromises[authMode] = refreshPromise;

  try {
    return await refreshPromise;
  } finally {
    delete refreshPromises[authMode];
  }
};

/**
 * Expose internal state for base-query's cleanup logic.
 */
export const getRefreshState = () => refreshState;
